/**
 * Frontend error tracking.
 *
 * The app shipped with no client-side error reporting at all: a white screen
 * on a vessel's tablet mid-inspection produced exactly zero signal on our
 * side, and the only report was a support email describing "it stopped".
 *
 * Enabled ONLY when VITE_SENTRY_DSN is set at build time. The import is
 * dynamic so the SDK never lands in the bundle graph of a build that doesn't
 * use it, and every entry point is a no-op when disabled — so nothing here can
 * break the app if the DSN is missing or the network blocks the endpoint.
 *
 * Privacy: inspection content, crew names and vessel data all flow through
 * this UI. We send no PII, no request bodies, and no breadcrumb payloads from
 * fetch/XHR bodies — only the error, the stack, and the route.
 */

interface SentryLike {
  init(opts: Record<string, unknown>): void;
  captureException(err: unknown, hint?: Record<string, unknown>): void;
  setUser(user: Record<string, unknown> | null): void;
}

let sentry: SentryLike | null = null;

const DSN = (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.VITE_SENTRY_DSN;
const MODE = (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.MODE ?? "production";

/** Initialise error tracking. Safe to call once at boot; no-op without a DSN. */
export async function initObservability(): Promise<void> {
  if (!DSN) return;
  try {
    const Sentry = (await import("@sentry/react")) as unknown as SentryLike;
    Sentry.init({
      dsn: DSN,
      environment: MODE,
      // Errors only. Performance tracing and session replay both capture far
      // more of the UI than is appropriate for inspection data.
      tracesSampleRate: 0,
      sendDefaultPii: false,
      beforeBreadcrumb(crumb: Record<string, unknown>) {
        // Drop the body of network breadcrumbs — they carry observation text.
        if (crumb.category === "fetch" || crumb.category === "xhr") {
          delete crumb.data;
        }
        return crumb;
      },
    });
    sentry = Sentry;
  } catch {
    // Blocked by an ad-blocker, offline, or the package isn't installed.
    // Reporting is strictly best-effort; never surface this to the user.
  }
}

/** Report an error. No-op when tracking is disabled; never throws. */
export function captureException(err: unknown, context?: Record<string, unknown>): void {
  if (!sentry) return;
  try {
    sentry.captureException(err, context ? { extra: context } : undefined);
  } catch { /* reporting must never break the app */ }
}

/**
 * Associate subsequent errors with a user — id and role only, never name or
 * email. Call on login; call with null on logout.
 */
export function identifyUser(user: { id: number; role: string; companyId?: number | null } | null): void {
  if (!sentry) return;
  try {
    sentry.setUser(user ? { id: String(user.id), role: user.role, companyId: user.companyId ?? null } : null);
  } catch { /* non-critical */ }
}
